import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { IconNotificationsOutline } from "@boligportal/bricks-icons";
import { Switch } from "@boligportal/bricks-ui";
import { createFileRoute } from "@tanstack/react-router";
import { type SanitizedSearchAgentProspect } from "~/api/shared/agent-sanitizer";
import { ProposalList } from "~/routes/(restricted)/byggeradar/-components/ProposalList";
import { ProposalPagination } from "~/routes/(restricted)/byggeradar/-components/ProposalPagination";
import { AgentDialog } from "~/routes/(restricted)/byggeradar/-components/dialogs/AgentDialog";
import { BaseMapLayout } from "~/routes/(restricted)/byggeradar/-components/layout/BaseMapLayout";
import { SearchResultMap } from "~/routes/(restricted)/byggeradar/-components/map/SearchResultMap";
import { ProposalSearchBar } from "~/routes/(restricted)/byggeradar/-components/navigation/ProposalSearchBar";
import { TOUR_ANCHORS } from "~/routes/(restricted)/byggeradar/-components/onboarding/tour-steps";
import { useCreateAgent } from "~/routes/(restricted)/byggeradar/-hooks/useCreateAgent";
import { useMapViewport } from "~/routes/(restricted)/byggeradar/-hooks/useMapViewport";
import { useProposalsLoader } from "~/routes/(restricted)/byggeradar/-hooks/useProposalsLoader";
import { useProposalsMapMarkers } from "~/routes/(restricted)/byggeradar/-hooks/useProposalsMapMarkers";
import { useSelectionData } from "~/routes/(restricted)/byggeradar/-hooks/useSelectionData";
import { useTrackSearchPerformed } from "~/routes/(restricted)/byggeradar/-hooks/useTrackSearchPerformed";
import { useUpdateSearch } from "~/routes/(restricted)/byggeradar/-hooks/useUpdateSearch";
import { proposalSearchSchema } from "~/routes/(restricted)/byggeradar/-schemas/proposal-search";
import { PAGINATION_PAGE_SIZE } from "~/routes/(restricted)/byggeradar/-utils/constants";
import { type ProposalSource } from "~/routes/(restricted)/byggeradar/-utils/proposal-source";

export const Route = createFileRoute("/(restricted)/byggeradar/_access/search")(
  {
    component: ProposalSearchPage,
    validateSearch: proposalSearchSchema,
    loader: async ({ context: { i18n } }) => {
      await i18n.loadNamespaces("proposals");

      const title = i18n.t("proposals:landing.meta.title");

      return { meta: { title } };
    },
    head: async (context) => {
      return {
        meta: [
          {
            title: context.loaderData?.meta.title,
          },
        ],
      };
    },
  },
);

function ProposalSearchPage() {
  const search = Route.useSearch();

  const { t } = useTranslation(["global", "proposals"]);

  const { updateSearch } = useUpdateSearch(search, Route.fullPath);

  const { page } = useSelectionData(search, updateSearch);

  const source = useMemo<ProposalSource>(
    () => ({ type: "filter", search }),
    [search],
  );

  const { boundingBox, onViewportChange, onReset, searchView } = useMapViewport(
    search,
    updateSearch,
  );

  const {
    isLoading,
    proposals,
    totalSize,
    error,
    isPlaceholderData,
    isMapNarrowed,
  } = useProposalsLoader(source, {
    size: PAGINATION_PAGE_SIZE,
    page: page,
    boundingBox,
  });

  useTrackSearchPerformed({
    search,
    isLoading,
    error,
    totalSize,
    isPlaceholderData,
    isMapNarrowed,
  });

  const {
    markers,
    isFetching: isMarkersFetching,
    error: markersError,
  } = useProposalsMapMarkers(source);

  const { createAgent, isCreating, createError } = useCreateAgent();

  const [showSaveAgentDialog, setShowSaveAgentDialog] = useState(false);
  const toggleShowSaveAgentDialog = () =>
    setShowSaveAgentDialog((state) => !state);

  const saveAgentClickHandler = async (
    prospect: SanitizedSearchAgentProspect,
  ) => {
    await createAgent(prospect);
    setShowSaveAgentDialog(false);
  };

  return (
    <>
      <BaseMapLayout
        topBarContent={
          <ProposalSearchBar
            search={search}
            routePath={Route.fullPath}
          />
        }
        listTopContent={
          <div className="flex flex-col gap-3">
            <div className="flex items-center text-xl font-bold">
              <div>
                {t("proposals:search.matches_title", {
                  amount: totalSize,
                })}
              </div>
            </div>
            <button
              data-tour={TOUR_ANCHORS.saveAgent}
              onClick={toggleShowSaveAgentDialog}
              className="flex w-full items-center gap-4 rounded-lg bg-linear-[90deg,#0D2A64_0%,#164193_100%] p-3 text-white"
            >
              <div className="flex size-10 shrink-0 items-center justify-center">
                <IconNotificationsOutline className="size-6" />
              </div>
              <div className="flex flex-col">
                <div className="text-left font-semibold">
                  {t("proposals:search.save_agent_cta.heading")}
                </div>
                <div className="text-primary-administration-200 text-left text-sm">
                  {t("proposals:search.save_agent_cta.description")}
                </div>
              </div>
              {/* Decorative only — signals interactivity; clicks fall through to the button */}
              <span
                inert
                className="ml-auto shrink-0 self-center pl-2"
              >
                <Switch.Root
                  as="span"
                  size="sm"
                  checked={false}
                >
                  <Switch.Control />
                </Switch.Root>
              </span>
            </button>
          </div>
        }
        listContent={
          <>
            <ProposalList
              isLoading={isLoading}
              proposals={proposals}
              error={error}
            />
            {totalSize && totalSize > PAGINATION_PAGE_SIZE ? (
              <div className="mt-5 flex w-full justify-center">
                <ProposalPagination
                  page={search.page ?? 0}
                  pageSize={PAGINATION_PAGE_SIZE}
                  totalItems={totalSize}
                  scrollSelector="#proposal-list"
                  updateSearch={updateSearch}
                />
              </div>
            ) : undefined}
          </>
        }
        mapContent={
          <SearchResultMap
            results={markers}
            source={source}
            isFetching={isMarkersFetching}
            error={markersError}
            routePath={Route.fullPath}
            searchView={searchView}
            onViewportChange={onViewportChange}
            onReset={onReset}
          />
        }
      />
      <AgentDialog
        isOpen={showSaveAgentDialog}
        mode="create"
        initialFilter={search}
        description={t("proposals:agents.dialogs.create.description")}
        close={toggleShowSaveAgentDialog}
        onSave={saveAgentClickHandler}
        isProcessing={isCreating}
        error={createError}
      />
    </>
  );
}
